import { useState, useEffect, useMemo, useCallback } from 'react';
import { getCountryOptions, getCountryLabel, matchesQuery } from './utils/countries';
import styles from './AdminCategory.module.css';
import LoadMoreButton from './components/LoadMoreButton';

const PLATFORMS = ['tiktok', 'youtube', 'instagram'];

const TIPS = {
  platform: 'SNS 플랫폼',
  country: '국가 코드 (ISO 3166-1 alpha-2)',
  cate: '카테고리',
  sub_cate: '서브 카테고리 (선택)',
  keyword: 'BrightData 검색 키워드',
  scrap: '데이터 수집 여부 (꺼지면 다음 스크랩부터 제외)',
  view: '프론트 페이지 노출 여부',
  comment_scrap: '댓글 수집 여부 (Scrap이 꺼지면 함께 꺼짐)',
};

function Toggle({ value, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : () => onChange(!value)}
      disabled={disabled}
      style={{
        width: 36, height: 20, borderRadius: 999,
        background: value ? '#16a34a' : '#ccc',
        border: 'none', position: 'relative',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <span style={{
        position: 'absolute', top: 2, left: value ? 18 : 2,
        width: 16, height: 16, borderRadius: '50%',
        background: 'white', transition: 'left 0.15s',
      }} />
    </button>
  );
}

function CountrySelect({ value, onChange }) {
  const options = useMemo(() => getCountryOptions(), []);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = useMemo(
    () => options.filter(o => matchesQuery(o, query)).slice(0, 100),
    [options, query]
  );

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        value={open ? query : (value ? getCountryLabel(value) : '')}
        placeholder="국가 검색 (한글/영문/코드)"
        onFocus={() => { setOpen(true); setQuery(''); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={e => setQuery(e.target.value)}
        style={inputStyle}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          maxHeight: 220, overflowY: 'auto',
          background: 'white', border: '1px solid #ddd', borderRadius: 6,
          zIndex: 10, marginTop: 2,
        }}>
          {filtered.map(o => (
            <div
              key={o.code}
              onMouseDown={() => { onChange(o.code); setOpen(false); }}
              style={{
                padding: '6px 10px', cursor: 'pointer',
                background: value === o.code ? '#f5f5f5' : 'white',
              }}
            >
              {o.label}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '6px 10px', color: '#888' }}>일치하는 국가 없음</div>
          )}
        </div>
      )}
    </div>
  );
}

function AddModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    platform: 'tiktok', country: '', cate: '', sub_cate: '', keyword: '',
    scrap: false, view: false, comment_scrap: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const canSubmit = form.country && form.cate.trim() && form.keyword.trim();

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || (data.errors && data.errors.join(', ')) || '오류');
        return;
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message || '네트워크 오류');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="카테고리 추가" onClose={onClose}>
      <Field label="Platform" tip={TIPS.platform}>
        <select
          value={form.platform}
          onChange={e => set('platform', e.target.value)}
          style={inputStyle}
        >
          {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </Field>
      <Field label="Country" tip={TIPS.country}>
        <CountrySelect value={form.country} onChange={v => set('country', v)} />
      </Field>
      <Field label="Cate" tip={TIPS.cate}>
        <input value={form.cate} onChange={e => set('cate', e.target.value)} placeholder="필수" style={inputStyle} />
      </Field>
      <Field label="Sub_cate" tip={TIPS.sub_cate}>
        <input value={form.sub_cate} onChange={e => set('sub_cate', e.target.value)} placeholder="선택" style={inputStyle} />
      </Field>
      <Field label="Keyword" tip={TIPS.keyword}>
        <input value={form.keyword} onChange={e => set('keyword', e.target.value)} placeholder="필수" style={inputStyle} />
      </Field>
      <Field label="Scrap" tip={TIPS.scrap}>
        <Toggle value={form.scrap} onChange={v => setForm(f => ({ ...f, scrap: v, comment_scrap: v ? f.comment_scrap : false }))} />
      </Field>
      <Field label="Comments" tip={TIPS.comment_scrap}>
        <Toggle value={form.comment_scrap} onChange={v => set('comment_scrap', v)} disabled={!form.scrap} />
      </Field>
      <Field label="View" tip={TIPS.view}>
        <Toggle value={form.view} onChange={v => set('view', v)} />
      </Field>

      {error && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button onClick={onClose} disabled={submitting} style={btnSecondary}>취소</button>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          style={{
            ...btnPrimary,
            ...((!canSubmit || submitting) && { background: '#ccc', cursor: 'not-allowed' }),
          }}
        >
          {submitting ? '저장 중...' : '저장'}
        </button>
      </div>
    </ModalShell>
  );
}

function EditModal({ row, onClose, onSaved }) {
  const [keyword, setKeyword] = useState(row.keyword);
  const [scrap, setScrap] = useState(row.scrap);
  const [view, setView] = useState(row.view);
  const [commentScrap, setCommentScrap] = useState(row.comment_scrap);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const dirty = keyword !== row.keyword || scrap !== row.scrap || view !== row.view || commentScrap !== row.comment_scrap;
  const canSubmit = dirty && keyword.trim().length > 0;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/categories/${row.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword: keyword.trim(), scrap, view, comment_scrap: scrap ? commentScrap : false }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || (data.errors && data.errors.join(', ')) || '오류');
        return;
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message || '네트워크 오류');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title={`카테고리 수정 #${row.id}`} onClose={onClose}>
      <Field label="Platform" tip={TIPS.platform}>
        <input value={row.platform} disabled style={{ ...inputStyle, background: '#f5f5f5' }} />
      </Field>
      <Field label="Country" tip={TIPS.country}>
        <input value={getCountryLabel(row.country)} disabled style={{ ...inputStyle, background: '#f5f5f5' }} />
      </Field>
      <Field label="Cate" tip={TIPS.cate}>
        <input value={row.cate} disabled style={{ ...inputStyle, background: '#f5f5f5' }} />
      </Field>
      <Field label="Sub_cate" tip={TIPS.sub_cate}>
        <input value={row.sub_cate || ''} disabled style={{ ...inputStyle, background: '#f5f5f5' }} />
      </Field>
      <Field label="Keyword" tip={TIPS.keyword}>
        <input value={keyword} onChange={e => setKeyword(e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Scrap" tip={TIPS.scrap}>
        <Toggle value={scrap} onChange={v => { setScrap(v); if (!v) setCommentScrap(false); }} />
      </Field>
      <Field label="Comments" tip={TIPS.comment_scrap}>
        <Toggle value={commentScrap} onChange={setCommentScrap} disabled={!scrap} />
      </Field>
      <Field label="View" tip={TIPS.view}>
        <Toggle value={view} onChange={setView} />
      </Field>

      {error && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button onClick={onClose} disabled={submitting} style={btnSecondary}>취소</button>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          style={{
            ...btnPrimary,
            ...((!canSubmit || submitting) && { background: '#ccc', cursor: 'not-allowed' }),
          }}
        >
          {submitting ? '저장 중...' : '저장'}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 12, padding: 24,
          minWidth: 400, maxWidth: 500, maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <h3 style={{ margin: '0 0 16px 0' }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Field({ label, tip, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label title={tip} style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 4, cursor: 'help' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, boxSizing: 'border-box',
};
const btnPrimary = {
  padding: '6px 16px', borderRadius: 6, border: 'none', background: '#3b82f6',
  color: 'white', fontWeight: 'bold', fontSize: 14, cursor: 'pointer',
};
const btnSecondary = {
  padding: '6px 16px', borderRadius: 6, border: '1px solid #ccc', background: 'white',
  color: '#333', fontSize: 14, cursor: 'pointer',
};

function CategoryManager() {
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(50);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/categories', { credentials: 'include' });
      const data = await res.json();
      setRows(data.data || []);
    } catch {
      setRows([]);
      setError('카테고리를 불러오지 못했습니다.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function updateField(id, patch) {
    const res = await fetch(`/api/admin/categories/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      alert('변경 실패: ' + (d.error || (d.errors && d.errors.join(', ')) || res.status));
      return;
    }
    await load();
  }

  const filtered = (rows || []).filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.platform.toLowerCase().includes(q) ||
      r.country.toLowerCase().includes(q) ||
      getCountryLabel(r.country).toLowerCase().includes(q) ||
      r.cate.toLowerCase().includes(q) ||
      (r.sub_cate || '').toLowerCase().includes(q) ||
      r.keyword.toLowerCase().includes(q)
    );
  });

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        <input
          className={styles.search}
          placeholder="플랫폼/국가/카테고리/키워드 검색"
          value={search}
          onChange={e => { setSearch(e.target.value); setVisibleCount(50); }}
        />
        <button className={styles.addBtn} onClick={() => setShowAdd(true)}>+ 추가</button>
      </div>

      {error && <p className={styles.error}>{error}</p>}
      {rows === null && <div className={styles.empty}>불러오는 중...</div>}
      {rows && filtered.length === 0 && (
        <div className={styles.empty}>{search ? '검색 결과 없음' : '카테고리 없음'}</div>
      )}
      {rows && filtered.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>ID</th>
              <th title={TIPS.platform}>Platform</th>
              <th title={TIPS.country}>Country</th>
              <th title={TIPS.cate}>Cate</th>
              <th title={TIPS.sub_cate}>Sub_cate</th>
              <th title={TIPS.keyword}>Keyword</th>
              <th title={TIPS.scrap}>Scrap</th>
              <th title={TIPS.comment_scrap}>Comments</th>
              <th title={TIPS.view}>View</th>
              <th>수정</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, visibleCount).map(r => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td>{r.platform}</td>
                <td>{getCountryLabel(r.country)}</td>
                <td>{r.cate}</td>
                <td>{r.sub_cate || '-'}</td>
                <td>{r.keyword}</td>
                <td><Toggle value={r.scrap} onChange={v => updateField(r.id, v ? { scrap: true } : { scrap: false, comment_scrap: false })} /></td>
                <td><Toggle value={r.comment_scrap} disabled={!r.scrap} onChange={v => updateField(r.id, { comment_scrap: v })} /></td>
                <td><Toggle value={r.view} onChange={v => updateField(r.id, { view: v })} /></td>
                <td><button onClick={() => setEditing(r)} className={styles.editBtn}>수정</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <LoadMoreButton hasMore={filtered.length > visibleCount} onClick={() => setVisibleCount(c => c + 50)} shown={Math.min(visibleCount, filtered.length)} total={filtered.length} />

      {showAdd && <AddModal onClose={() => setShowAdd(false)} onSaved={load} />}
      {editing && <EditModal row={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

export default CategoryManager;
